/**
 * The money logic behind adding and editing a financial transaction: the form
 * schemas, the create payload, the smallest PATCH, whether that PATCH replaces
 * the row, and which words a refusal gets.
 *
 * Pure, and no imports from api-client (the dependency runs the other way), so
 * the row and problem shapes here are the parts of the generated ones read.
 * Schema messages and `ftxErrorKey` results are full i18n keys: pass them to `t`.
 */
import { z } from "zod";
import { currencyDecimals } from "./format/money";
import { normalizeDigits } from "./format/normalizeDigits";

const FORM_ERROR = "payroll:admin.ftx.form.errors.";
const NAME_ERROR = "payroll:admin.ftx.names.errors.";
const ERROR = "payroll:admin.ftx.errors.";

/** Most files one transaction can carry. */
export const FTX_MAX_ATTACHMENTS = 10;
/** Most ids a bulk delete accepts. */
export const FTX_BULK_DELETE_MAX = 200;
const FTX_NOTE_MAX = 500;
const FTX_NAME_MAX = 60;

/** `sourceRefType` of a row a payroll inputs import wrote. */
export const FTX_IMPORT_SOURCE = "PAYROLL_INPUT_IMPORT";

// ---------------------------------------------------------------------------
// Form schemas
// ---------------------------------------------------------------------------

export interface FtxFormValues {
  employeeId: string;
  /** Empty only on an older row saved without a name, which an edit may leave as it is. */
  nameId: string;
  /** As typed, Arabic digits included. `ftxAmountNumber` reads it. */
  amount: string;
  effectiveDate: string;
  offCycle: boolean;
  paidCash: boolean;
  note: string;
}

/** What an edit of a `one_time:` row can change. */
export interface FtxOneTimeFormValues {
  amount: string;
  note: string;
}

export type FtxNameType = "BONUS" | "REIMBURSEMENT" | "DEDUCTION";

export interface FtxNameFormValues {
  nameEn: string;
  nameAr: string;
  ftType: FtxNameType;
  active: boolean;
}

export interface FtxSchemaOptions {
  /** The company's currency, for its decimal places. Null while it loads: the JOD limit. */
  currency?: string | null;
  /** False on an edit of a row that has no name yet. */
  nameRequired?: boolean;
}

/** The amount as a number. Only meaningful once the schema has passed it. */
export function ftxAmountNumber(raw: string): number {
  return Number(normalizeDigits(raw).trim());
}

function amountIssue(raw: string, decimals: number, signed: boolean): string | null {
  const text = normalizeDigits(raw).trim();
  if (text === "") return `${FORM_ERROR}amount_required`;
  if (!(signed ? /^-?\d+(\.\d+)?$/ : /^\d+(\.\d+)?$/).test(text)) {
    return `${FORM_ERROR}amount_invalid`;
  }
  if (!signed && Number(text) <= 0) return `${FORM_ERROR}amount_positive`;
  const fraction = text.split(".")[1]?.replace(/0+$/, "") ?? "";
  return fraction.length > decimals ? `${FORM_ERROR}amount_scale` : null;
}

function amountSchema(currency: string | null | undefined, signed: boolean): z.ZodType<string> {
  const decimals = currency === null || currency === undefined ? 3 : currencyDecimals(currency);
  return z.string().superRefine((raw, ctx) => {
    const message = amountIssue(raw, decimals, signed);
    if (message !== null) ctx.addIssue({ code: z.ZodIssueCode.custom, message });
  });
}

const noteSchema = z.string().max(FTX_NOTE_MAX, `${FORM_ERROR}note_too_long`);

export function ftxFormSchema(options: FtxSchemaOptions = {}): z.ZodType<FtxFormValues> {
  return z.object({
    employeeId: z.string().min(1, `${FORM_ERROR}employee_required`),
    nameId:
      options.nameRequired === false ? z.string() : z.string().min(1, `${FORM_ERROR}name_required`),
    amount: amountSchema(options.currency, false),
    effectiveDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, `${FORM_ERROR}date_required`),
    offCycle: z.boolean(),
    paidCash: z.boolean(),
    note: noteSchema,
  });
}

/** A one-time row's amount keeps its sign: the server decides what each kind allows. */
export function ftxOneTimeFormSchema(
  options: Pick<FtxSchemaOptions, "currency"> = {},
): z.ZodType<FtxOneTimeFormValues> {
  return z.object({ amount: amountSchema(options.currency, true), note: noteSchema });
}

export function ftxNameFormSchema(): z.ZodType<FtxNameFormValues> {
  return z.object({
    nameEn: z
      .string()
      .trim()
      .min(1, `${NAME_ERROR}name_en_required`)
      .max(FTX_NAME_MAX, `${NAME_ERROR}too_long`),
    nameAr: z
      .string()
      .trim()
      .min(1, `${NAME_ERROR}name_ar_required`)
      .max(FTX_NAME_MAX, `${NAME_ERROR}too_long`),
    ftType: z.enum(["BONUS", "REIMBURSEMENT", "DEDUCTION"], {
      errorMap: () => ({ message: `${NAME_ERROR}kind_required` }),
    }),
    active: z.boolean(),
  });
}

// ---------------------------------------------------------------------------
// Payloads
// ---------------------------------------------------------------------------

export interface FtxCreatePayload {
  employeeId: string;
  nameId: string;
  amount: number;
  currencyCode: string;
  effectiveDate: string;
  note?: string;
  offCycle?: boolean;
  paidCash?: boolean;
  attachments?: string[];
}

/** `POST /financial-transactions`, by `nameId`, never `type` and `category`. */
export function ftxCreatePayload(
  values: FtxFormValues,
  currencyCode: string,
  attachments: readonly string[] = [],
): FtxCreatePayload {
  const note = values.note.trim();
  return {
    employeeId: values.employeeId,
    nameId: values.nameId,
    amount: ftxAmountNumber(values.amount),
    currencyCode,
    effectiveDate: values.effectiveDate,
    ...(note !== "" ? { note } : {}),
    ...(values.offCycle ? { offCycle: true, paidCash: values.paidCash } : {}),
    ...(attachments.length > 0 ? { attachments: [...attachments] } : {}),
  };
}

/** The parts of the loaded `FinancialTransactionView` a diff compares against. */
export interface FtxEditSource {
  type: string;
  amount: number;
  effectiveDate: string;
  offCycle: boolean;
  nameId?: string | null;
  paidCash?: boolean | null;
  note?: string | null;
  sourceRefType?: string | null;
}

export interface FtxPatchDiff {
  amount?: number;
  nameId?: string;
  effectiveDate?: string;
  offCycle?: boolean;
  paidCash?: boolean;
  note?: string;
  attachments?: string[];
}

/** The form's starting values for a loaded row. */
export function ftxFormValuesFrom(source: FtxEditSource & { employeeId: string }): FtxFormValues {
  return {
    employeeId: source.employeeId,
    nameId: source.nameId ?? "",
    amount: String(source.amount),
    effectiveDate: source.effectiveDate,
    offCycle: source.offCycle,
    paidCash: source.paidCash ?? false,
    note: source.note ?? "",
  };
}

/**
 * The smallest `PATCH /financial-transactions/{id}`: absent means unchanged on
 * the server, and sending a field that didn't move could still be refused on an
 * imported or paid row. A cleared note is sent as "", which the server stores.
 */
export function ftxPatchDiff(
  source: FtxEditSource,
  values: FtxFormValues,
  newAttachments: readonly string[] = [],
): FtxPatchDiff {
  const amount = ftxAmountNumber(values.amount);
  const note = values.note.trim();
  // Paid cash only means something on an off-cycle row.
  const paidCash = values.offCycle && values.paidCash;
  return {
    ...(amount !== source.amount ? { amount } : {}),
    ...(values.nameId !== "" && values.nameId !== (source.nameId ?? "")
      ? { nameId: values.nameId }
      : {}),
    ...(values.effectiveDate !== source.effectiveDate
      ? { effectiveDate: values.effectiveDate }
      : {}),
    ...(values.offCycle !== source.offCycle ? { offCycle: values.offCycle } : {}),
    ...(paidCash !== (source.paidCash ?? false) ? { paidCash } : {}),
    ...(note !== (source.note ?? "").trim() ? { note } : {}),
    ...(newAttachments.length > 0 ? { attachments: [...newAttachments] } : {}),
  };
}

/**
 * Whether saving this diff replaces the row: a new id, PENDING, the old one
 * cancelled. `nextType` is the type of the name just picked, because a name of
 * another type changes the type. An imported row has no approval chain, so its
 * amount changes in place. Only a warning: the response's id is the truth.
 */
export function isFtxMoneyEdit(
  diff: FtxPatchDiff,
  source: Pick<FtxEditSource, "type" | "sourceRefType">,
  nextType?: string | null,
): boolean {
  if (source.sourceRefType === FTX_IMPORT_SOURCE) return false;
  return (
    diff.amount !== undefined ||
    diff.effectiveDate !== undefined ||
    diff.offCycle !== undefined ||
    (nextType !== undefined && nextType !== null && nextType !== source.type)
  );
}

export interface FtxOneTimePatchDiff {
  amount?: number;
  notes?: string;
}

/** The smallest `PATCH /one-time-payments/{id}`. The row keeps its id. */
export function ftxOneTimePatchDiff(
  source: { amount: number; note?: string | null },
  values: FtxOneTimeFormValues,
): FtxOneTimePatchDiff {
  const amount = ftxAmountNumber(values.amount);
  const notes = values.note.trim();
  return {
    ...(amount !== source.amount ? { amount } : {}),
    ...(notes !== (source.note ?? "").trim() ? { notes } : {}),
  };
}

// ---------------------------------------------------------------------------
// Refusals
// ---------------------------------------------------------------------------

/** The parts of an `ApiError` read here. */
export interface FtxProblem {
  errorCode?: string;
  status?: number;
  extensions?: Record<string, unknown>;
}

const ERROR_KEY_BY_CODE: Readonly<Record<string, string>> = {
  FT_MANAGED_BY_INPUT_IMPORT: "managed_by_import",
  FT_SYSTEM_MANAGED: "system_managed",
  FT_OFF_CYCLE_PAID: "off_cycle_paid",
  FT_OFF_CYCLE_NOT_PAYABLE: "off_cycle_not_payable",
  FT_OFF_CYCLE_NO_PAY_RUN: "off_cycle_no_pay_run",
  FT_NAME_NOT_FOUND: "name_unavailable",
  FT_NAME_INACTIVE: "name_unavailable",
  FT_NAME_DUPLICATE: "name_duplicate",
  FT_ATTACHMENT_NOT_FOUND: "attachment_not_found",
  FT_NOT_FOUND: "not_found",
  FT_NOT_PENDING: "not_pending",
  FT_BULK_DELETE_REFUSED: "bulk_refused",
  ONE_TIME_PAYMENT_NOT_FOUND: "one_time_not_found",
  ONE_TIME_PAYMENT_NOT_PENDING: "one_time_not_pending",
  ONE_TIME_PAYMENT_SIGN_INVALID: "one_time_sign_invalid",
  VALIDATION_FAILED: "validation_failed",
  SOURCE_NOT_DELETABLE: "source_not_deletable",
  MONEY_SCALE_UNSUPPORTED: "money_scale",
  FILE_TOO_LARGE: "file_too_large",
  STORAGE_UNSUPPORTED_CONTENT_TYPE: "unsupported_file_type",
};

function argsOf(error: FtxProblem): Record<string, unknown> {
  const args = error.extensions?.["args"];
  return typeof args === "object" && args !== null ? (args as Record<string, unknown>) : {};
}

function keyForCode(code: string | undefined): string | null {
  const key = code === undefined ? undefined : ERROR_KEY_BY_CODE[code];
  return key === undefined ? null : `${ERROR}${key}`;
}

/**
 * The i18n key for a refusal, or null when the code is not one of ours: then
 * show the server's `title`. "Try again shortly" and "closed" are the same 409,
 * told apart by `runStatus`.
 */
export function ftxErrorKey(error: FtxProblem): string | null {
  if (error.status === 403) return "errors:generic.forbidden";
  if (error.errorCode === "PAYROLL_INPUT_PERIOD_LOCKED") {
    const { runStatus } = argsOf(error);
    if (runStatus === "COMPUTING") return `${ERROR}period_recalculating`;
    return typeof runStatus === "string" ? `${ERROR}period_closed` : `${ERROR}period_locked`;
  }
  return keyForCode(error.errorCode);
}

export interface FtxBulkRefusal {
  /** The composite ledger id of the refused row, when the server named one. */
  id: string | null;
  /** Why, in words. The reason carries no `runStatus`, so a lock reads either way. */
  reasonKey: string;
}

/** The row an all-or-nothing delete stopped on, or null for any other error. */
export function ftxBulkRefusal(error: FtxProblem): FtxBulkRefusal | null {
  if (error.errorCode !== "FT_BULK_DELETE_REFUSED") return null;
  const { id, reason } = argsOf(error);
  const code = typeof reason === "string" ? reason : undefined;
  return {
    id: typeof id === "string" ? id : null,
    reasonKey:
      code === "PAYROLL_INPUT_PERIOD_LOCKED"
        ? `${ERROR}period_locked`
        : (keyForCode(code) ?? `${ERROR}bulk_reason_unknown`),
  };
}

export type FtxFormField = keyof FtxFormValues;

const VIOLATION_FIELD: Readonly<Record<string, FtxFormField>> = {
  employeeId: "employeeId",
  nameId: "nameId",
  type: "nameId",
  category: "nameId",
  amount: "amount",
  effectiveDate: "effectiveDate",
  offCycle: "offCycle",
  paidCash: "paidCash",
  note: "note",
  notes: "note",
};

/** `VALIDATION_FAILED`'s `violations[]`, mapped to form fields. Others are dropped. */
export function ftxViolations(error: FtxProblem): Array<{ field: FtxFormField; message: string }> {
  const raw = error.extensions?.["violations"];
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry: unknown) => {
    if (typeof entry !== "object" || entry === null) return [];
    const { field, message } = entry as { field?: unknown; message?: unknown };
    const mapped = typeof field === "string" ? VIOLATION_FIELD[field] : undefined;
    return mapped === undefined || typeof message !== "string" ? [] : [{ field: mapped, message }];
  });
}
